import {
  type CloudTier,
  cloudTierPricing,
  cloudTiers,
} from '@ipfabric/utils/license/baseCloudConstruct';
import type { ChartPalette } from './licenseChartPalette';

/** Categories of the `/tables/settings/administration/license-consumption/overview` table. */
const DEVICE_CATEGORY = 'Device';
const CLOUD_CONSTRUCT_CATEGORY = 'Cloud Construct';

/** At most this many rows per breakdown, the last one aggregating the tail. */
const MAX_DEVICE_ITEMS = 6;
const MAX_SEGMENTS_PER_TIER = 3;

/** Short form, used where the tier stands next to a number. */
export const TIER_LABELS: Record<CloudTier, string> = {
  A: 'Tier A',
  B: 'Tier B',
  C: 'Tier C',
};

/** A tier whose constructs don't bill: 0 CCU and no device license, see `cloudTierPricing`. */
const isNonBillableTier = (tier: CloudTier): boolean =>
  cloudTierPricing[tier].isUnlicensed;

/** What one construct of the tier costs, e.g. `1 CCU per construct` or `non-billable`. */
export const formatTierCost = (tier: CloudTier): string =>
  isNonBillableTier(tier)
    ? 'non-billable'
    : `${cloudTierPricing[tier].ccu} CCU per construct`;

const tierAxisLabel = (tier: CloudTier): string =>
  isNonBillableTier(tier)
    ? `${TIER_LABELS[tier]} (non-billable)`
    : TIER_LABELS[tier];

/** Axis form, flagging the tiers that don't count towards consumption. */
export const TIER_AXIS_LABELS: Record<CloudTier, string> = {
  A: tierAxisLabel('A'),
  B: tierAxisLabel('B'),
  C: tierAxisLabel('C'),
};

const UNKNOWN_TIER_LABEL = 'Unknown tier';

/** One row of the overview table, with the columns this feature declares mandatory. */
export type OverviewRow = {
  category: string;
  type: string;
  tier?: string | null;
  /**
   * A string in practice, despite the endpoint's OAS declaring a number: `count(*)` is a Postgres
   * bigint, the query does not cast it, and node-postgres keeps bigints as strings to preserve
   * precision. Read it through `toCount`, never as a number.
   */
  count: number | string;
  proportion: number;
};

export type BreakdownItem = {
  label: string;
  count: number;
  /** Share of the row's own category, as computed by the endpoint. */
  proportion: number;
  color: string;
};

export type DeviceBreakdown = {
  items: BreakdownItem[];
  /** Sum of every Device row, including the ones folded into "Other". */
  total: number;
};

export type TierBar = {
  tier: CloudTier | null;
  label: string;
  total: number;
  /** From the base of the bar upwards. */
  segments: BreakdownItem[];
};

export type ConstructBreakdown = {
  bars: TierBar[];
  legend: BreakdownItem[];
};

/** Largest rows kept as they are, plus the aggregate of everything below the cut. */
type CappedItems = {
  items: BreakdownItem[];
  other?: BreakdownItem;
};

/**
 * Counts must be coerced before any arithmetic - added as strings they concatenate, which silently
 * turns an aggregate of 5, 14 and 18 into 51418.
 */
const toCount = (value: number | string): number => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const sumCounts = (rows: OverviewRow[]): number =>
  rows.reduce((sum, row) => sum + toCount(row.count), 0);

const byRowCountDesc = (a: OverviewRow, b: OverviewRow) =>
  toCount(b.count) - toCount(a.count);

const byCountDesc = (a: { count: number }, b: { count: number }) =>
  b.count - a.count;

const isCloudTier = (value: unknown): value is CloudTier =>
  (cloudTiers as readonly unknown[]).includes(value);

/**
 * Keep the largest rows and fold the remainder into one aggregate, so a long tail of small types
 * does not turn the legend into a wall of 1% rows.
 */
const capWithOther = (
  rows: OverviewRow[],
  maxItems: number,
  toLabel: (row: OverviewRow) => string,
  otherLabel: string,
  colors: string[],
): CappedItems => {
  const sorted = [...rows].sort(byRowCountDesc);
  const kept =
    sorted.length > maxItems ? sorted.slice(0, maxItems - 1) : sorted;
  const folded = sorted.slice(kept.length);

  const colorAt = (index: number) => colors[index % colors.length] ?? '';
  const items = kept.map((row, index) => ({
    label: toLabel(row),
    count: toCount(row.count),
    proportion: row.proportion,
    color: colorAt(index),
  }));

  if (folded.length === 0) {
    return { items };
  }
  return {
    items,
    other: {
      label: otherLabel,
      count: sumCounts(folded),
      proportion: folded.reduce((sum, row) => sum + row.proportion, 0),
      color: colorAt(items.length),
    },
  };
};

const allItems = ({ items, other }: CappedItems): BreakdownItem[] =>
  other === undefined ? items : [...items, other];

export const toDeviceBreakdown = (
  rows: OverviewRow[],
  colors: ChartPalette['device'],
): DeviceBreakdown => {
  const deviceRows = rows.filter((row) => row.category === DEVICE_CATEGORY);
  return {
    items: allItems(
      capWithOther(
        deviceRows,
        MAX_DEVICE_ITEMS,
        (row) => row.type,
        'Other',
        colors,
      ),
    ),
    total: sumCounts(deviceRows),
  };
};

/**
 * Unknown tiers reuse the Tier C ramp: `cloudTier` is nullable on `vDevice`, and a neutral grey is a
 * better answer than dropping those constructs from the chart entirely.
 */
const tierColors = (
  tier: CloudTier | null,
  colors: ChartPalette['tier'],
): string[] => colors[tier ?? 'C'];

type TierBarWithCap = { bar: TierBar; capped: CappedItems };

const toTierBar = (
  tier: CloudTier | null,
  rows: OverviewRow[],
  colors: ChartPalette['tier'],
): TierBarWithCap => {
  const capped = capWithOther(
    rows,
    MAX_SEGMENTS_PER_TIER,
    (row) => `${row.type} [${tier ?? '?'}]`,
    tier === null ? 'Other' : `Other ${TIER_LABELS[tier]}`,
    tierColors(tier, colors),
  );
  return {
    capped,
    bar: {
      tier,
      label: tier === null ? UNKNOWN_TIER_LABEL : TIER_AXIS_LABELS[tier],
      total: sumCounts(rows),
      segments: allItems(capped),
    },
  };
};

/**
 * Order the legend by size across all tiers, but keep every "Other" aggregate directly after the
 * last row of the tier it belongs to, so a bar's segments stay recognisable in the list.
 */
const toLegend = (bars: TierBarWithCap[]): BreakdownItem[] => {
  const entries = bars.flatMap(({ bar, capped }) =>
    capped.items.map((item) => ({ item, bar })),
  );
  const ordered = [...entries].sort((a, b) => byCountDesc(a.item, b.item));

  return ordered.flatMap(({ item, bar }, index) => {
    const isLastOfTier = !ordered
      .slice(index + 1)
      .some((next) => next.bar === bar);
    const other = isLastOfTier
      ? bars.find((candidate) => candidate.bar === bar)?.capped.other
      : undefined;
    return other === undefined ? [item] : [item, other];
  });
};

export const toConstructBreakdown = (
  rows: OverviewRow[],
  colors: ChartPalette['tier'],
): ConstructBreakdown => {
  const constructRows = rows.filter(
    (row) => row.category === CLOUD_CONSTRUCT_CATEGORY,
  );
  const knownTierBars = cloudTiers
    .map((tier) =>
      toTierBar(
        tier,
        constructRows.filter((row) => row.tier === tier),
        colors,
      ),
    )
    .filter(({ bar }) => bar.segments.length > 0);
  const unknownTierRows = constructRows.filter((row) => !isCloudTier(row.tier));
  const bars =
    unknownTierRows.length > 0
      ? [...knownTierBars, toTierBar(null, unknownTierRows, colors)]
      : knownTierBars;

  return { bars: bars.map(({ bar }) => bar), legend: toLegend(bars) };
};

export type TierCcuSegment = {
  tier: CloudTier;
  label: string;
  ccuCount: number;
};

/**
 * Per-tier CCU sums for the cloud summary bar, in tier order.
 *
 * The API sends them as `[['tierA', 218], ['tierB', 164], ['tierC', 0]]` - keys prefixed, values
 * either numbers or numeric strings. Tiers that consume nothing are dropped, which is what leaves
 * Tier C out of the bar.
 */
export const toTierCcuSegments = (tierCcuCounts: unknown): TierCcuSegment[] => {
  if (!Array.isArray(tierCcuCounts)) {
    return [];
  }
  const ccuByTier = new Map<CloudTier, number>();
  for (const entry of tierCcuCounts) {
    if (!Array.isArray(entry)) {
      continue;
    }
    const [key, value] = entry;
    const tier = typeof key === 'string' ? key.replace(/^tier/, '') : '';
    const ccuCount = Number(value);
    if (isCloudTier(tier) && Number.isFinite(ccuCount) && ccuCount > 0) {
      ccuByTier.set(tier, ccuCount);
    }
  }
  return cloudTiers
    .filter((tier) => ccuByTier.has(tier))
    .map((tier) => ({
      tier,
      label: TIER_LABELS[tier],
      ccuCount: ccuByTier.get(tier) ?? 0,
    }));
};
